import { EditOutlined, UnorderedListOutlined } from "@ant-design/icons"
import {
  Button,
  Checkbox,
  type CheckboxChangeEvent,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  Typography
} from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import type { BaseOptionType } from "antd/lib/select"
import React, { useCallback, useEffect, useState } from "react"

import {
  AddressAutoComplete,
  type SuggestionAddress,
  type SuggestionCityZip
} from "../../../../../components/AutoComplete/AddressAutoComplete.tsx"
import { ZipLookupHint } from "../../../../../components/AutoComplete/ZipLookupHint.tsx"
import { placeholders } from "../../../../../constants/placeholders"
import { suggestionZip } from "../../../../../components/AutoComplete/suggestion-zip.ts"
import { useKnownStates } from "../../../../../hooks/use-known-states.ts"
import { useZipLookup } from "../../../../../hooks/use-zip-lookup.ts"
import { useZipModeToggle } from "../../../../../hooks/use-zip-mode-toggle.ts"
import { validateZipCode } from "../../../../../utils/validation/zip-code.ts"
import { type ProjectFormData } from "../project-form-data.ts"
import { ProjectAddressMapPreview } from "../shared/ProjectAddressMapPreview.tsx"

interface PropertyInformationSectionProps {
  form: FormInstance<ProjectFormData>
  options: Record<string, BaseOptionType[]>
  initialZip?: string | null
}

export function PropertyInformationSection({
  form,
  options,
  initialZip
}: PropertyInformationSectionProps) {
  const [zipOptions, setZipOptions] = useState<SuggestionCityZip[]>([])

  const isCustomerAddress = Form.useWatch("use_customer_address", form)
  const customer = Form.useWatch("customer", form)

  const hasMultipleZips = zipOptions?.length > 1

  const knownStates = useKnownStates(options)
  const { lookupZip, chooseMatch, suppressLookupFor, clearStatus, status } = useZipLookup({
    knownStates,
    disabled: isCustomerAddress,
    initialZip,
    onFill: (fill) => form.setFieldsValue(fill)
  })

  const { isCustomZip, toggleZipMode } = useZipModeToggle(
    form,
    "zip_code",
    clearStatus
  )

  const onSetPropertyByCustomer = useCallback(
    (customer: ProjectFormData["customer"]) => {
      suppressLookupFor(customer?.zip_code)
      form.setFieldsValue({
        address: customer?.address || null,
        address_2: customer?.address_2 || null,
        state: customer?.state || null,
        city: customer?.city || null,
        zip_code: customer?.zip_code || null
      })
    },
    [form, suppressLookupFor]
  )

  const onChangeUseCustomerAddress = (e: CheckboxChangeEvent) => {
    const customer = form.getFieldsValue()?.customer
    if (e?.target?.checked) onSetPropertyByCustomer(customer)
  }

  const onAddressSelect = ({ address, city_zips }: SuggestionAddress) => {
    const zip = suggestionZip({ address, city_zips })

    suppressLookupFor(zip)
    form.setFieldsValue({
      address: address?.street || null,
      city: address?.city || null,
      state: address?.state || null,
      zip_code: zip
    })

    setZipOptions(city_zips)
  }

  const onZipCodeSelect = (zipCode: string) => {
    const selectedZip = zipOptions.find((z) => z.zip_code === zipCode)
    if (selectedZip) {
      suppressLookupFor(selectedZip?.zip_code)
      form.setFieldsValue({
        zip_code: selectedZip?.zip_code,
        city: selectedZip?.city,
        state: selectedZip?.state
      })
    }
  }


  useEffect(() => {
    if (isCustomerAddress) {
      onSetPropertyByCustomer(customer)
    }
  }, [isCustomerAddress, customer, onSetPropertyByCustomer])

  return (
    <>
      <Form.Item<ProjectFormData>
        name="use_customer_address"
        valuePropName="checked"
      >
        <Checkbox onChange={onChangeUseCustomerAddress}>
          <Typography.Text>Apply Customer Address</Typography.Text>
        </Checkbox>
      </Form.Item>

      <Form.Item<ProjectFormData> label="Address Search">
        <AddressAutoComplete
          disabled={isCustomerAddress}
          onSelect={onAddressSelect}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectFormData>
            label="Address 1"
            name={"address"}
            rules={[
              {
                required: false,
                message: "Please enter address"
              }
            ]}
          >
            <Input disabled={isCustomerAddress} placeholder="Enter Address" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<ProjectFormData> label="Address 2" name={"address_2"}>
            <Input disabled={isCustomerAddress} placeholder="Enter Address 2" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={10}>
          <Form.Item<ProjectFormData>
            label="City"
            name="city"
            rules={[
              {
                required: false,
                message: "Please enter city"
              }
            ]}
          >
            <Input disabled={isCustomerAddress} placeholder="Enter City" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={10}>
          <Form.Item<ProjectFormData>
            label="State"
            name={"state"}
            rules={[
              {
                required: false,
                message: "Please enter state"
              }
            ]}
            normalize={(value: string) => value ?? null}
          >
            <Select
              allowClear
              disabled={isCustomerAddress}
              placeholder="Enter State"
              options={options?.state}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input?.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={4}>
          <Form.Item<ProjectFormData> label="ZIP Code">
            <Flex gap={"small"}>
              <Form.Item<ProjectFormData>
                name={"zip_code"}
                noStyle
                rules={[
                  {
                    required: false,
                    validator: validateZipCode
                  }
                ]}
              >
                {hasMultipleZips && !isCustomZip ? (
                  <Select
                    disabled={isCustomerAddress}
                    style={{ flex: 1 }}
                    allowClear
                    placeholder={placeholders.zipCode}
                    onSelect={onZipCodeSelect}
                    options={zipOptions.map((z) => ({
                      value: z.zip_code,
                      label: z.zip_code
                    }))}
                  />
                ) : (
                  <Input
                    disabled={isCustomerAddress}
                    style={{ flex: 1 }}
                    placeholder={placeholders.zipCode}
                    onChange={(event) => lookupZip(event.target.value)}
                  />
                )}
              </Form.Item>

              {hasMultipleZips && (
                <Tooltip
                  placement={"topLeft"}
                  title={isCustomZip ? "Show list" : "Enter custom"}
                >
                  <Button
                    icon={
                      isCustomZip ? <UnorderedListOutlined /> : <EditOutlined />
                    }
                    onClick={toggleZipMode}
                  />
                </Tooltip>
              )}
            </Flex>
            <ZipLookupHint status={status} onChoose={chooseMatch} />
          </Form.Item>
        </Col>
      </Row>

      {/* Map Preview */}
      <ProjectAddressMapPreview form={form} />
    </>
  )
}
